import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PropertyReport } from '../components/PropertyReport';
import { MonthlyDataManager } from '../components/property/MonthlyDataManager';
import { PropertyUserManager } from '../components/property/PropertyUserManager';
import { Property, User } from '../types/user';
import { MonthlyStats } from '../types/property';
import { getPropertyById } from '../services/properties';
import { getMonthlyStats } from '../services/stats';
import { getUsers } from '../services/users';
import { Card } from '../components/ui/Card';
import toast from 'react-hot-toast';

export const PropertyDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [property, setProperty] = useState<Property | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [propertyUsers, setPropertyUsers] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'stats' | 'users'>(
    searchParams.get('tab') === 'users' ? 'users' : 'stats'
  );

  const fetchData = async () => {
    if (!id) return;

    try {
      const [propertyData, statsData, usersData] = await Promise.all([
        getPropertyById(id),
        getMonthlyStats(id),
        getUsers()
      ]);

      if (!propertyData) {
        throw new Error('Propiedad no encontrada');
      }

      setProperty(propertyData);
      setMonthlyStats(statsData);
      
      // Filter users for this property
      const propertyUsersList = usersData.filter(user => user.propertyId === id);
      const availableUsersList = usersData.filter(user => 
        user.role === 'owner' && (!user.propertyId || user.propertyId === id)
      );
      
      setPropertyUsers(propertyUsersList);
      setAvailableUsers(availableUsersList);
    } catch (error) {
      console.error('Error al cargar los datos:', error);
      toast.error('Error al cargar los datos de la propiedad');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!property) {
    return (
      <DashboardLayout>
        <div className="text-center text-gray-500">
          No se encontró la propiedad
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PropertyReport property={property} monthlyStats={monthlyStats} />
        
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-4 px-6 text-sm font-medium ${
                  activeTab === 'stats'
                    ? 'border-b-2 border-red-500 text-red-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Estadísticas
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`py-4 px-6 text-sm font-medium ${
                  activeTab === 'users'
                    ? 'border-b-2 border-red-500 text-red-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Usuarios
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'stats' ? (
              <MonthlyDataManager
                propertyId={property.id}
                monthlyStats={monthlyStats}
                onDataChange={fetchData}
              />
            ) : (
              <PropertyUserManager
                propertyId={property.id}
                propertyUsers={propertyUsers}
                availableUsers={availableUsers}
                onUsersChange={fetchData}
              />
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};